import React, { useState, AnchorHTMLAttributes } from "react";
import "./Link.css";

export interface LinkProps
  extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "className"> {
  /**
   * Texto del enlace
   */
  children: React.ReactNode;

  /**
   * URL del enlace
   */
  href: string;

  /**
   * Si el enlace está deshabilitado
   */
  disabled?: boolean;

  /**
   * Si el enlace debe mostrar un icono externo
   */
  showExternalIcon?: boolean;

  /**
   * Si el enlace debe abrirse en una nueva pestaña
   */
  openInNewTab?: boolean;

  /**
   * Si el enlace está en estado de focus (para demo)
   */
  forceFocus?: boolean;

  /**
   * Clase CSS adicional
   */
  className?: string;
}

/**
 * Componente Link para navegación con diferentes estados
 */
export const Link: React.FC<LinkProps> = ({
  children,
  href,
  disabled = false,
  showExternalIcon = false,
  openInNewTab = false,
  forceFocus = false,
  className = "",
  ...props
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(forceFocus);

  const containerClasses = [
    "qida-link",
    disabled ? "qida-link--disabled" : "",
    isFocused ? "qida-link--focused" : "",
    isHovered ? "qida-link--hovered" : "",
    showExternalIcon ? "qida-link--with-icon" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  if (disabled) {
    return (
      <span className={containerClasses}>
        <span className="qida-link__text">{children}</span>
        {showExternalIcon && (
          <span className="qida-link__icon">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6 3V4H11.59L3 12.59L3.71 13.3L12.3 4.7V10.3H13.3V3H6Z"
                fill="currentColor"
              />
            </svg>
          </span>
        )}
      </span>
    );
  }

  return (
    <a
      className={containerClasses}
      href={href}
      target={openInNewTab ? "_blank" : undefined}
      rel={openInNewTab ? "noopener noreferrer" : undefined}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      tabIndex={0}
      {...props}
    >
      <span className="qida-link__text">{children}</span>
      {showExternalIcon && (
        <span className="qida-link__icon">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6 3V4H11.59L3 12.59L3.71 13.3L12.3 4.7V10.3H13.3V3H6Z"
              fill="currentColor"
            />
          </svg>
        </span>
      )}
    </a>
  );
};

export default Link;
